import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function StandardDeviationCalc() {
  const [input, setInput] = useState('10, 12, 23, 23, 16, 23, 21, 16');
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const arr = input.split(/[,\s]+/).map(x => parseFloat(x)).filter(x => !isNaN(x));
    if (arr.length === 0) return null;
    const n = arr.length;
    const sum = arr.reduce((s, v) => s + v, 0);
    const mean = sum / n;
    const dev = arr.map(v => v - mean);
    const sqDev = dev.map(d => d * d);
    const varPop = sqDev.reduce((s, v) => s + v, 0) / n;
    const varSample = sqDev.reduce((s, v) => s + v, 0) / (n - 1 || 1);
    return { arr, dev, sqDev, n, sum, mean, sdPop: Math.sqrt(varPop), sdSample: Math.sqrt(varSample), varPop, varSample, min: Math.min(...arr), max: Math.max(...arr) };
  }, [input]);

  return (
    <CalcShell look="cnet"
      title="Standard Deviation Calculator"
      subtitle="Compute mean, variance and standard deviation for a data set with visual charts."
      about={<>
        <p><b>Standard deviation (σ)</b> measures how spread out numbers are from their mean. A low SD means the numbers are close to the mean, a high SD means they are spread out.</p>
        <p>Use <b>population SD</b> when the data represents the entire population, and <b>sample SD</b> when it's a sample used to estimate the population (Bessel correction: n − 1).</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Data (comma or space separated)</label>
            <textarea rows={5} value={input} onChange={e => setInput(e.target.value)} />
          </div>
        </div>
        <div>
          {c && (
            <table className="data-table">
              <tbody>
                <tr><td>Count (n)</td><td className="text-right font-semibold">{c.n}</td></tr>
                <tr><td>Sum</td><td className="text-right">{c.sum.toFixed(4)}</td></tr>
                <tr><td>Mean (μ)</td><td className="text-right">{c.mean.toFixed(6)}</td></tr>
                <tr><td>Min / Max</td><td className="text-right">{c.min} / {c.max}</td></tr>
                <tr><td>Variance (population σ²)</td><td className="text-right">{c.varPop.toFixed(6)}</td></tr>
                <tr><td>Variance (sample s²)</td><td className="text-right">{c.varSample.toFixed(6)}</td></tr>
                <tr><td>SD (population σ)</td><td className="text-right font-semibold text-blue-700">{c.sdPop.toFixed(6)}</td></tr>
                <tr><td>SD (sample s)</td><td className="text-right font-semibold">{c.sdSample.toFixed(6)}</td></tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {c && (
        <>
          {/* Deviation breakdown table */}
          <div className="mt-6 overflow-auto">
            <h3 className="font-bold text-slate-900 mb-2">Deviation breakdown</h3>
            <table className="w-full text-sm border border-slate-200">
              <thead className="bg-slate-100"><tr>
                <th className="px-3 py-2 text-left">x</th>
                <th className="px-3 py-2 text-right">x − μ</th>
                <th className="px-3 py-2 text-right">(x − μ)²</th>
              </tr></thead>
              <tbody>
                {c.arr.map((v, i) => (
                  <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-2">{v}</td>
                    <td className="px-3 py-2 text-right" style={{ color: c.dev[i] >= 0 ? '#059669' : '#dc2626' }}>{c.dev[i].toFixed(4)}</td>
                    <td className="px-3 py-2 text-right">{c.sqDev[i].toFixed(4)}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50 font-semibold">
                  <td className="px-3 py-2">Σ</td>
                  <td className="px-3 py-2 text-right">0.0000</td>
                  <td className="px-3 py-2 text-right">{c.sqDev.reduce((s, v) => s + v, 0).toFixed(4)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Data point bar chart */}
            <div className="bg-slate-50 rounded-2xl p-4">
              <h3 className="font-bold text-slate-900 mb-2">Data points vs Mean</h3>
              <DataBars arr={c.arr} mean={c.mean} min={c.min} max={c.max} />
            </div>
            {/* Normal distribution bell curve */}
            <div className="bg-slate-50 rounded-2xl p-4">
              <h3 className="font-bold text-slate-900 mb-2">Normal Distribution (μ, σ)</h3>
              <BellCurve mean={c.mean} sd={c.sdPop} />
              <div className="flex flex-wrap gap-3 justify-center text-xs mt-2">
                <Legend color="#93c5fd" label="±1σ (68%)" />
                <Legend color="#60a5fa" label="±2σ (95%)" />
                <Legend color="#3b82f6" label="±3σ (99.7%)" />
              </div>
            </div>
          </div>
        </>
      )}

      {c && (
        <>
          {/* Margin of Error (Confidence Interval) */}
          <div className="mt-6">
            <h3 className="font-bold text-slate-900 mb-2">Margin of Error (Confidence Interval)</h3>
            <p className="text-sm text-slate-600 mb-3">
              Standard Error of the Mean, SEM = σ / √n = {c.sdPop.toFixed(4)} / √{c.n} = <b>{(c.sdPop / Math.sqrt(c.n)).toFixed(6)}</b>
            </p>
            <MarginOfErrorTable mean={c.mean} sem={c.sdPop / Math.sqrt(c.n)} />
          </div>

          {/* Frequency Table */}
          <div className="mt-6">
            <h3 className="font-bold text-slate-900 mb-2">Frequency Table</h3>
            <FrequencyTable arr={c.arr} />
          </div>

          {/* Histogram overlay with Normal Curve */}
          <div className="mt-6">
            <h3 className="font-bold text-slate-900 mb-2">Histogram &amp; Fitted Normal Curve</h3>
            <SDHistogram arr={c.arr} mean={c.mean} sd={c.sdPop} />
          </div>

          {/* Show Working Toggle */}
          <div className="mt-6">
            <button onClick={() => setShowWork(v => !v)} data-testid="sd-show-work-toggle"
              className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
              {showWork ? '− Hide working' : '+ Show working'}
            </button>
            {showWork && (
              <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="sd-work-panel">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
                <ol className="list-decimal ml-5 space-y-3 text-slate-800 text-[15px] leading-7">
                  <li>
                    <b>Sum the data.</b> <span className="font-mono">Σx = {c.arr.join(' + ')} = {c.sum}</span>
                  </li>
                  <li>
                    <b>Compute the mean.</b> <span className="font-mono">μ = Σx / n = {c.sum} / {c.n} = {c.mean.toFixed(6)}</span>
                  </li>
                  <li>
                    <b>Compute each deviation (x − μ) and square it.</b>
                    <div className="mt-1 overflow-auto">
                      <table className="text-sm border border-slate-200">
                        <thead className="bg-white"><tr><th className="px-2 py-1 text-left">x</th><th className="px-2 py-1 text-right">x − μ</th><th className="px-2 py-1 text-right">(x − μ)²</th></tr></thead>
                        <tbody>
                          {c.arr.map((v, i) => (
                            <tr key={i}>
                              <td className="px-2 py-1 font-mono">{v}</td>
                              <td className="px-2 py-1 text-right font-mono">{v} − {c.mean.toFixed(4)} = {c.dev[i].toFixed(4)}</td>
                              <td className="px-2 py-1 text-right font-mono">{c.dev[i].toFixed(4)}² = {c.sqDev[i].toFixed(4)}</td>
                            </tr>
                          ))}
                          <tr className="bg-blue-100 font-semibold">
                            <td className="px-2 py-1">Σ</td>
                            <td className="px-2 py-1 text-right font-mono">0.0000</td>
                            <td className="px-2 py-1 text-right font-mono">{c.sqDev.reduce((s, v) => s + v, 0).toFixed(4)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </li>
                  <li>
                    <b>Population variance.</b>
                    <div className="font-mono">σ² = Σ(x − μ)² / n = {c.sqDev.reduce((s, v) => s + v, 0).toFixed(4)} / {c.n} = <b>{c.varPop.toFixed(6)}</b></div>
                  </li>
                  <li>
                    <b>Population standard deviation.</b>
                    <div className="font-mono">σ = √σ² = √{c.varPop.toFixed(4)} = <b className="text-[#4c7b25]">{c.sdPop.toFixed(6)}</b></div>
                  </li>
                  <li>
                    <b>Sample variance (Bessel's correction, uses n − 1).</b>
                    <div className="font-mono">s² = Σ(x − μ)² / (n − 1) = {c.sqDev.reduce((s, v) => s + v, 0).toFixed(4)} / {c.n - 1} = <b>{c.varSample.toFixed(6)}</b></div>
                  </li>
                  <li>
                    <b>Sample standard deviation.</b>
                    <div className="font-mono">s = √s² = √{c.varSample.toFixed(4)} = <b className="text-[#4c7b25]">{c.sdSample.toFixed(6)}</b></div>
                  </li>
                  <li>
                    <b>Standard Error of the Mean.</b>
                    <div className="font-mono">SEM = σ / √n = {c.sdPop.toFixed(4)} / √{c.n} = <b>{(c.sdPop / Math.sqrt(c.n)).toFixed(6)}</b></div>
                  </li>
                </ol>
              </div>
            )}
          </div>
        </>
      )}

      {c && <StepsPanel steps={[
        `Compute mean μ = Σx / n = ${c.sum.toFixed(2)} / ${c.n} = <b>${c.mean.toFixed(4)}</b>`,
        `Compute deviations (x − μ), then squared deviations (x − μ)²`,
        `Sum of squared deviations Σ(x − μ)² = <b>${c.sqDev.reduce((s, v) => s + v, 0).toFixed(4)}</b>`,
        `Population variance σ² = Σ(x − μ)² / n = <b>${c.varPop.toFixed(4)}</b>`,
        `Population SD σ = √σ² = <b>${c.sdPop.toFixed(4)}</b>`,
        `Sample SD s = √[Σ(x − μ)² / (n − 1)] = <b>${c.sdSample.toFixed(4)}</b>`,
        `Standard Error of Mean, SEM = σ / √n = <b>${(c.sdPop / Math.sqrt(c.n)).toFixed(6)}</b>`
      ]} />}
    </CalcShell>
  );
}

function MarginOfErrorTable({ mean, sem }) {
  // Common CI multipliers
  const levels = [
    { level: '68%', z: 1.000 },
    { level: '90%', z: 1.645 },
    { level: '95%', z: 1.960 },
    { level: '99%', z: 2.576 },
    { level: '99.5%', z: 2.807 },
    { level: '99.9%', z: 3.291 },
  ];
  const maxMoe = Math.max(...levels.map(l => l.z * sem)) || 1;
  return (
    <table className="w-full text-sm border border-slate-200">
      <thead className="bg-slate-100"><tr>
        <th className="px-3 py-2 text-left">Confidence Level</th>
        <th className="px-3 py-2 text-left">Margin of Error</th>
        <th className="px-3 py-2 text-left">Interval / Error Bar</th>
      </tr></thead>
      <tbody>
        {levels.map(l => {
          const moe = l.z * sem;
          const w = Math.max(4, (moe / maxMoe) * 220);
          return (
            <tr key={l.level} className="border-t border-slate-100">
              <td className="px-3 py-2">{l.level}</td>
              <td className="px-3 py-2 font-mono">{mean.toFixed(4)} ± {moe.toFixed(4)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  <div className="relative h-2 bg-slate-100 rounded" style={{ width: 260 }}>
                    <div className="absolute top-1/2 -translate-y-1/2 bg-blue-500 h-2 rounded" style={{ left: 130 - w / 2, width: w }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-1.5 h-3 bg-red-500 rounded-sm" style={{ left: 130 - 3 }} />
                  </div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FrequencyTable({ arr }) {
  const freq = new Map();
  arr.forEach(v => freq.set(v, (freq.get(v) || 0) + 1));
  const rows = [...freq.entries()].sort((a, b) => a[0] - b[0]);
  const n = arr.length;
  return (
    <table className="w-full text-sm border border-slate-200">
      <thead className="bg-slate-100"><tr>
        <th className="px-3 py-2 text-left">Data Value</th>
        <th className="px-3 py-2 text-right">Frequency</th>
        <th className="px-3 py-2 text-right">Relative Frequency</th>
      </tr></thead>
      <tbody>
        {rows.map(([v, f], i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
            <td className="px-3 py-2">{v}</td>
            <td className="px-3 py-2 text-right">{f}</td>
            <td className="px-3 py-2 text-right">{((f / n) * 100).toFixed(2)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-700">
      <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }}></span>
      <span>{label}</span>
    </div>
  );
}

function DataBars({ arr, mean, min, max }) {
  const w = 400, h = 200, pad = 40;
  const range = (max - min) || 1;
  const barW = (w - 2 * pad) / arr.length;
  const meanY = h - pad - ((mean - min) / range) * (h - 2 * pad);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#94a3b8" />
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#94a3b8" />
      {arr.map((v, i) => {
        const x = pad + i * barW + barW * 0.15;
        const bh = ((v - min) / range) * (h - 2 * pad);
        const y = h - pad - bh;
        const above = v >= mean;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW * 0.7} height={bh} fill={above ? '#3b82f6' : '#f59e0b'} opacity="0.85" />
            <text x={x + barW * 0.35} y={y - 3} fontSize="10" fill="#334155" textAnchor="middle">{v}</text>
          </g>
        );
      })}
      <line x1={pad} y1={meanY} x2={w - pad} y2={meanY} stroke="#ef4444" strokeWidth="2" strokeDasharray="5 3" />
      <text x={w - pad + 4} y={meanY + 4} fontSize="10" fill="#dc2626" fontWeight="700">μ = {mean.toFixed(2)}</text>
    </svg>
  );
}

function BellCurve({ mean, sd }) {
  if (sd <= 0) sd = 1;
  const w = 400, h = 200, pad = 20;
  const xMin = mean - 4 * sd, xMax = mean + 4 * sd;
  const pdf = x => (1 / (sd * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mean) / sd, 2));
  const pmax = pdf(mean);
  const tx = x => pad + (x - xMin) / (xMax - xMin) * (w - 2 * pad);
  const ty = y => h - pad - (y / pmax) * (h - 2 * pad);
  const pts = [];
  for (let i = 0; i <= 200; i++) {
    const x = xMin + (i / 200) * (xMax - xMin);
    pts.push([tx(x), ty(pdf(x))]);
  }
  // Region paths for ±1σ, ±2σ, ±3σ
  const region = (lo, hi, color) => {
    const seg = pts.filter(p => {
      const x = xMin + ((p[0] - pad) / (w - 2 * pad)) * (xMax - xMin);
      return x >= mean - hi * sd && x <= mean + hi * sd && (x <= mean - lo * sd || x >= mean + lo * sd || lo === 0);
    });
    if (!seg.length) return null;
    const d = `M${seg[0][0]},${h - pad} ` + seg.map(p => `L${p[0]},${p[1]}`).join(' ') + ` L${seg[seg.length - 1][0]},${h - pad} Z`;
    return <path d={d} fill={color} opacity="0.7" />;
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      {/* Grid */}
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#cbd5e1" />
      {region(0, 1, '#93c5fd')}
      {region(1, 2, '#60a5fa')}
      {region(2, 3, '#3b82f6')}
      <polyline points={pts.map(p => p.join(',')).join(' ')} fill="none" stroke="#1e3a8a" strokeWidth="2" />
      {/* SD lines */}
      {[-3, -2, -1, 0, 1, 2, 3].map(k => (
        <g key={k}>
          <line x1={tx(mean + k * sd)} y1={h - pad} x2={tx(mean + k * sd)} y2={ty(pdf(mean + k * sd))} stroke="#0f172a" strokeWidth="0.5" strokeDasharray="2 2" />
          <text x={tx(mean + k * sd)} y={h - pad + 12} fontSize="9" fill="#64748b" textAnchor="middle">{k === 0 ? 'μ' : (k > 0 ? '+' : '') + k + 'σ'}</text>
        </g>
      ))}
    </svg>
  );
}

function SDHistogram({ arr, mean, sd }) {
  if (!arr || arr.length === 0) return null;
  const bins = Math.max(6, Math.min(20, Math.ceil(Math.sqrt(arr.length))));
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  const range = (max - min) || 1;
  const binW = range / bins;
  const counts = new Array(bins).fill(0);
  arr.forEach(v => {
    let idx = Math.floor((v - min) / binW);
    if (idx >= bins) idx = bins - 1;
    counts[idx]++;
  });
  const maxCount = Math.max(...counts) || 1;
  const w = 440, h = 220, pad = 30;
  const tx = x => pad + ((x - min) / range) * (w - 2 * pad);

  // Normal PDF scaled to bin area (freq * binW)
  const pdf = x => (1 / (sd * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mean) / sd, 2));
  const totalArea = arr.length * binW; // expected bar count = pdf * totalArea for each bin
  const ymax = Math.max(maxCount, pdf(mean) * totalArea) || 1;
  const ty = c => h - pad - (c / ymax) * (h - 2 * pad - 12);

  // Build normal curve polyline
  const curvePts = [];
  for (let i = 0; i <= 200; i++) {
    const x = min + (i / 200) * range;
    curvePts.push([tx(x), ty(pdf(x) * totalArea)]);
  }

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#334155" />
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#334155" />
      {counts.map((c, i) => {
        const x0 = tx(min + i * binW), x1 = tx(min + (i + 1) * binW);
        const y = ty(c);
        return (
          <g key={i}>
            <rect x={x0 + 1} y={y} width={x1 - x0 - 2} height={h - pad - y}
              fill="#93c5fd" stroke="#1e40af" strokeWidth="1" />
            {c > 0 && <text x={(x0 + x1) / 2} y={y - 3} fontSize="10" fill="#334155" textAnchor="middle">{c}</text>}
          </g>
        );
      })}
      {/* Overlay: fitted normal curve */}
      <polyline points={curvePts.map(p => p.join(',')).join(' ')} fill="none" stroke="#dc2626" strokeWidth="2" />
      {/* Mean line */}
      <line x1={tx(mean)} y1={pad} x2={tx(mean)} y2={h - pad} stroke="#059669" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x={tx(mean) + 4} y={pad + 12} fontSize="10" fill="#059669" fontWeight="700">μ = {mean.toFixed(2)}</text>
      {/* ±1σ shading endpoints */}
      {[-1, 1].map(k => (
        <line key={k} x1={tx(mean + k * sd)} y1={pad + 20} x2={tx(mean + k * sd)} y2={h - pad}
          stroke="#7c3aed" strokeWidth="1" strokeDasharray="3 3" />
      ))}
      <text x={tx(mean - sd) + 3} y={pad + 32} fontSize="9" fill="#7c3aed">−1σ</text>
      <text x={tx(mean + sd) + 3} y={pad + 32} fontSize="9" fill="#7c3aed">+1σ</text>
      {/* x-axis ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const v = min + t * range;
        return <text key={t} x={pad + t * (w - 2 * pad)} y={h - pad + 15} fontSize="9" fill="#64748b" textAnchor="middle">{v.toFixed(1)}</text>;
      })}
      <text x={w / 2} y={h - 4} fontSize="10" fill="#64748b" textAnchor="middle">Value</text>
      <text x={pad + 40} y={pad + 8} fontSize="10" fill="#dc2626">Fitted normal curve</text>
    </svg>
  );
}
